"use client";
import "@vidstack/react/player/styles/default/theme.css";
import "@vidstack/react/player/styles/default/layouts/audio.css";
import "@vidstack/react/player/styles/default/layouts/video.css";
import * as React from "react";
import {
	MediaPlayer,
	MediaPlayerInstance,
	MediaProvider,
	Poster,
} from "@vidstack/react";
import {
	defaultLayoutIcons,
	DefaultVideoLayout,
} from "@vidstack/react/player/layouts/default";
import Hls from "hls.js";
import { useEffect, useMemo } from "react";
import { stegaClean } from "@sanity/client/stega";
import { IMuxVideoPlayer } from "@/sanity/globals/VideoPlayer";

interface HLSVideoPlayerProps extends React.ComponentPropsWithRef<"video"> {
	source: IMuxVideoPlayer;
}

export const HLSVideoPlayer = React.forwardRef<
	HTMLVideoElement,
	HLSVideoPlayerProps
>(function HLSVideoPlayer(
	props: HLSVideoPlayerProps,
	ref: React.ForwardedRef<HTMLVideoElement>,
) {
	const videoRef = React.useRef<HTMLVideoElement>();
	const hlsRef = React.useRef<Hls>();
	const { source, ...rest } = props;
	const cleanedVideoSrc = useMemo(() => {
		return stegaClean(source?.muxVideo?.playbackId ?? "");
	}, [source]);

	useEffect(() => {
		if (Hls.isSupported() && source?.muxVideo?.playbackId && videoRef.current) {
			hlsRef.current = new Hls({
				fetchSetup: function (context, initParams: RequestInit) {
					initParams.mode = "no-cors";
					return new Request(context.url, initParams);
				},
			});
			hlsRef.current.loadSource(
				`https://stream.mux.com/${cleanedVideoSrc}.m3u8?redundant_streams=true`,
			);
			hlsRef.current.attachMedia(videoRef.current);
		}

		return () => {
			if (hlsRef.current && videoRef.current) {
				hlsRef.current.detachMedia();
				hlsRef.current.destroy();
			}
		};
	}, [cleanedVideoSrc, source]);

	return (
		<video
			ref={(node) => {
				if (node) {
					videoRef.current = node;
				}

				if (ref) {
					if (typeof ref === "function") {
						ref(node);
					} else {
						ref.current = node;
					}
				}
			}}
			{...rest}
			crossOrigin="anonymous"
		/>
	);
});

export interface VideoPlayerProps
	extends React.HTMLAttributes<MediaPlayerInstance> {
	playerData: IMuxVideoPlayer;
	playsinline?: boolean;
	disableClickToToggle?: boolean;
	autoPlay?: boolean;
	muted?: boolean;
	loop?: boolean;
	hideDefaultLayout?: boolean;
	poster?: string;
}
const SanityVideoPlayer = React.forwardRef<
	MediaPlayerInstance,
	VideoPlayerProps
>(({ playerData, className, playsinline = false, disableClickToToggle = false, autoPlay = false, muted = false, loop = false, hideDefaultLayout = false, poster, ...props }, ref) => {
	const cleanedVideoSrc = useMemo(() => {
		return playerData ? stegaClean(playerData) : playerData;
	}, [playerData]);

	const playbackId = (cleanedVideoSrc as IMuxVideoPlayer)?.muxVideo?.playbackId;

	// Prefer the explicit poster prop, then the player's own thumbnail. Never an
	// empty string — an `<img src="">` triggers a full page re-download in React.
	const posterSrc = poster || (playerData?.thumbnail?.src ?? "") || undefined;

	// Mux storyboard for the scrubber preview thumbnails. Only built when we have
	// a real playbackId — otherwise vidstack renders an `<img src="">` for the
	// preview and the browser warns + refetches the page.
	const storyboardSrc = playbackId
		? `https://image.mux.com/${playbackId}/storyboard.vtt`
		: null;

	const playerRef = React.useRef<MediaPlayerInstance>(null);

	React.useImperativeHandle(ref, () => playerRef.current as MediaPlayerInstance);

	const handleClick = (e: React.MouseEvent) => {
		if (disableClickToToggle) return;

		// Check if click is on a control element
		const target = e.target as HTMLElement;
		if (target.closest('.vds-controls, .vds-control, button, [role="button"], [role="slider"]')) {
			return; // Let the control handle it
		}

		// Toggle play/pause
		if (playerRef.current) {
			if (playerRef.current.paused) {
				playerRef.current.play();
			} else {
				playerRef.current.pause();
			}
		}
	};

	return (
		<div onClick={handleClick} className={disableClickToToggle ? "w-full h-full" : "w-full h-full cursor-pointer"}>
			<MediaPlayer
				ref={playerRef}
				className={className}
				src={
					(playerData?.embed
						? playerData?.embedUrl
						: `https://stream.mux.com/${playbackId}.m3u8?redundant_streams=true`) ??
					""
				}
				poster={posterSrc}
				playsInline={playsinline}
				autoPlay={autoPlay}
				muted={muted}
				loop={loop}
				// @ts-ignore
				onPause={props?.onPause}
				// @ts-ignore
				onPlay={props?.onPlay}
			>
				<MediaProvider></MediaProvider>
				{!hideDefaultLayout && (
					<DefaultVideoLayout
						className="!rounded-[inherit] overflow-clip block absolute top-0 start-0 w-full h-full"
						icons={defaultLayoutIcons}
						noScrubGesture={false}
						thumbnails={storyboardSrc}
					/>
				)}
				{posterSrc && (
					<Poster
						className="vds-poster !w-full !h-full object-cover !rounded-[inherit]"
						src={posterSrc}
						alt=""
					/>
				)}
			</MediaPlayer>
		</div>
	);
});

SanityVideoPlayer.displayName = "SanityVideoPlayer";
export { SanityVideoPlayer };

